import { supabase } from './supabase';

export interface ResultShare {
  id: string;
  result_id: string;
  shared_by: string;
  recipient_type: 'coach' | 'partner';
  recipient_coach_id: string | null;
  recipient_partner_id: string | null;
  message: string | null;
  status: 'pending' | 'accepted' | 'revoked';
  created_at: string;
  updated_at: string;
  recipient_name?: string;
  result_completed_at?: string;
}

export interface SearchResult {
  id: string;
  type: 'coach' | 'partner';
  name: string;
  email: string;
  company?: string;
}

export async function searchCoachesAndPartners(email: string): Promise<SearchResult[]> {
  const trimmed = email.trim();
  if (!trimmed || !trimmed.includes('@')) return [];

  const { data, error } = await supabase
    .rpc('lookup_share_recipient_by_email', { p_email: trimmed });

  if (error || !data) return [];

  return (data as any[]).map((row) => ({
    id: row.recipient_id,
    type: row.recipient_type as 'coach' | 'partner',
    name: row.display_name,
    email: trimmed,
  }));
}

export async function createShare(
  resultId: string,
  sharedBy: string,
  recipient: SearchResult,
  message?: string
): Promise<{ error: string | null }> {
  const insert: any = {
    result_id: resultId,
    shared_by: sharedBy,
    recipient_type: recipient.type,
    message: message || null,
    status: 'pending',
  };

  if (recipient.type === 'coach') {
    insert.recipient_coach_id = recipient.id;
  } else {
    insert.recipient_partner_id = recipient.id;
  }

  const { error } = await supabase.from('result_shares').insert(insert);
  return { error: error?.message || null };
}

export async function fetchSharesByUser(userId: string): Promise<ResultShare[]> {
  const { data } = await supabase
    .from('result_shares')
    .select(`
      id, result_id, shared_by, recipient_type, recipient_coach_id, recipient_partner_id,
      message, status, created_at, updated_at,
      coaches:result_shares_recipient_coach_id_fkey(user_id, users!coaches_user_id_fkey(full_name)),
      partners:result_shares_recipient_partner_id_fkey(company_name, contact_email, user_id, users!partners_user_id_fkey(full_name)),
      results!result_shares_result_id_fkey(completed_at)
    `)
    .eq('shared_by', userId)
    .order('created_at', { ascending: false });

  return (data || []).map((row: any) => {
    let recipient_name = 'Unknown';
    if (row.recipient_type === 'coach' && row.coaches) {
      recipient_name = row.coaches.users?.full_name || 'Coach';
    } else if (row.recipient_type === 'partner' && row.partners) {
      recipient_name = row.partners.company_name || row.partners.users?.full_name || 'Partner';
    }
    return {
      id: row.id,
      result_id: row.result_id,
      shared_by: row.shared_by,
      recipient_type: row.recipient_type,
      recipient_coach_id: row.recipient_coach_id,
      recipient_partner_id: row.recipient_partner_id,
      message: row.message,
      status: row.status,
      created_at: row.created_at,
      updated_at: row.updated_at,
      recipient_name,
      result_completed_at: row.results?.completed_at,
    } as ResultShare;
  });
}

export async function revokeShare(shareId: string): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('result_shares')
    .update({ status: 'revoked' })
    .eq('id', shareId);
  return { error: error?.message || null };
}

export async function fetchReceivedSharesCoach(coachId: string): Promise<ResultShare[]> {
  const { data } = await supabase
    .rpc('fetch_received_shares', { p_recipient_type: 'coach', p_recipient_id: coachId });

  return (data || []).map((row: any) => ({
    id: row.share_id,
    result_id: row.result_id,
    shared_by: row.shared_by,
    recipient_type: 'coach' as const,
    recipient_coach_id: coachId,
    recipient_partner_id: null,
    message: row.message,
    status: row.status,
    created_at: row.created_at,
    updated_at: row.created_at,
    recipient_name: row.sharer_name || 'Respondent',
    result_completed_at: row.result_completed_at,
  })) as ResultShare[];
}

export async function fetchReceivedSharesPartner(partnerId: string): Promise<ResultShare[]> {
  const { data } = await supabase
    .rpc('fetch_received_shares', { p_recipient_type: 'partner', p_recipient_id: partnerId });

  return (data || []).map((row: any) => ({
    id: row.share_id,
    result_id: row.result_id,
    shared_by: row.shared_by,
    recipient_type: 'partner' as const,
    recipient_coach_id: null,
    recipient_partner_id: partnerId,
    message: row.message,
    status: row.status,
    created_at: row.created_at,
    updated_at: row.created_at,
    recipient_name: row.sharer_name || 'Respondent',
    result_completed_at: row.result_completed_at,
  })) as ResultShare[];
}

export async function acceptShare(shareId: string): Promise<{ error: string | null }> {
  const { error } = await supabase
    .from('result_shares')
    .update({ status: 'accepted' })
    .eq('id', shareId);
  return { error: error?.message || null };
}
